import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useLocation, Link } from "wouter";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import CertificateCard from "@/components/CertificateCard";
import SkillCategoriesDisplay from "@/components/skills/components/SkillCategoriesDisplay";
import { calculateActivitySkills } from "@/lib/activityUtils";
import { createCompanySlug } from "@/lib/companyUtils";
import { getCompanyDisplayInfo } from "@/lib/companyDisplayUtils";
import { findActivityBySlug } from "@/lib/activityUtils";
import { calculateMonthsBetween } from "@/lib/utils";
import { encodeSkillName } from "@/lib/skills";
import DualCompanyLogo from "@/components/ui/dual-company-logo";
import DualCompanyName from "@/components/ui/dual-company-name";
import { UnifiedProjectsComponent } from '@/components/UnifiedProjects';
import { activities, companies, certificates, certificateActivities } from '../../../db';

type ActivityParams = {
  slug: string;
};

export default function ActivityPage({ params }: { params: ActivityParams }) {
  const [location, setLocation] = useLocation();
  const activity = findActivityBySlug(params.slug);
  const displayInfo = activity ? getCompanyDisplayInfo(activity, companies) : null;
  
  // Calculate skills for this activity
  const activitySkills = activity ? calculateActivitySkills(activity.id) : {
    profession: [],
    lead: [],
    language: [],
    tools: [],
  };
  
  // Get certificates related to this activity
  const activityCertificateIds = activity ? certificateActivities
    .filter(ca => ca.activityId === activity.id)
    .map(ca => ca.certificateId) : [];
  const activityCertificates = certificates
    .filter(cert => activityCertificateIds.includes(cert.id) && cert.isVisible)
    .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime());

  if (!activity || !displayInfo) {
    setLocation("/404");
    return null;
  }

  return (
    <main className="container mx-auto p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="mb-6 bg-background/80 backdrop-blur">
          <CardHeader>
            <div className="flex items-center gap-4">
              {displayInfo.isDualCompany ? (
                <DualCompanyLogo
                  assignmentCompany={displayInfo.assignmentCompany}
                  tempAgency={displayInfo.tempAgency!}
                  size="lg"
                  onAssignmentCompanyClick={() => {
                    setLocation(`/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`);
                  }}
                  onTempAgencyClick={() => {
                    setLocation(`/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`);
                  }}
                />
              ) : (
                <Link href={`/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`}>
                  <a>
                    <Avatar className="h-12 w-12 bg-transparent cursor-pointer hover:scale-105 transition-transform">
                      <AvatarImage
                        src={displayInfo.assignmentCompany.logoPath || undefined}
                        alt={`${displayInfo.assignmentCompany.name} logo`}
                        className="object-cover bg-transparent"
                      />
                      <AvatarFallback className="bg-primary/10">{displayInfo.assignmentCompany.logoText}</AvatarFallback>
                    </Avatar>
                  </a>
                </Link>
              )}
              <div>
                <CardTitle className="text-2xl">{activity.title}</CardTitle>
                {displayInfo.isDualCompany ? (
                  <DualCompanyName
                    assignmentCompany={displayInfo.assignmentCompany}
                    tempAgency={displayInfo.tempAgency!}
                    className="text-muted-foreground"
                  />
                ) : (
                  <p className="text-muted-foreground">{displayInfo.assignmentCompany.name}</p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Duration</h3>
                <p className="text-muted-foreground">
                  {new Date(activity.startDate).toLocaleDateString()} - {activity.endDate ? new Date(activity.endDate).toLocaleDateString() : 'Present'}
                </p>
              </div>
              {activity.department && (
                <div>
                  <h3 className="font-semibold mb-2">Department</h3>
                  <p className="text-muted-foreground">{activity.department}</p>
                </div>
              )}
              {activity.description && (
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-muted-foreground">{activity.description}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skills Section */}
        {Object.values(activitySkills).some(categorySkills => categorySkills.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6"
          >
            <Card className="bg-background/80 backdrop-blur">
              <CardHeader>
                <CardTitle>Skills & Technologies Developed</CardTitle>
              </CardHeader>
              <CardContent>
                <SkillCategoriesDisplay 
                  skillsByCategory={activitySkills}
                  displayMode="progress"
                  compactMode={false}
                  animated={true}
                  calculationType="activity"
                  activityId={activity.id}
                  showHeader={false}
                />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <UnifiedProjectsComponent
            activityId={activity.id}
            showPrivateProjects={false}
            defaultViewMode="list"
            showCompanyLogos={false}
            pageTitle="Projects"
            emptyStateMessage="No projects found for this activity."
          />
        </motion.div>

        {/* Certificates Section */}
        {activityCertificates.length > 0 && (
          <Card className="bg-background/80 backdrop-blur mt-6">
            <CardHeader>
              <CardTitle>Zugehörige Zertifizierungen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activityCertificates.map((certificate, index) => (
                  <CertificateCard 
                    key={certificate.id} 
                    certificate={certificate} 
                    compact={true}
                    index={index}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </main>
  );
}
